// Adapted from aec-project-controls/src/domain/ifcMeshSource.ts (same author, MIT OR
// Apache-2.0), itself a port of ifc-engine-core/demo/viewer-core/src/mesh-source.ts
// (commit 341c08d7) — see src/vendor/ifc-mesh-wasm/NOTICE.md for the exact pin.
// Decode contract unchanged from the Controls port: this file is a byte-for-byte carry-over of
// the wasm-facing wrapper, not a re-derivation.

export const VERTEX_STRIDE_BYTES = 12;
export const POSITION_QUANT_MAX = 65535;
const SUPPORTED_MESH_BUFFER_VERSION = 1;
const SUPPORTED_MANIFEST_VERSION = 1;

export type BboxMm = readonly [number, number, number, number, number, number];
export type IndexType = "u16" | "u32";

export interface VertexAttribute {
  readonly name: string;
  readonly type: string;
  readonly count: number;
  readonly offset: number;
}

export interface BufferRange {
  readonly byte_offset: number;
  readonly count: number;
}

export interface IndexRange extends BufferRange {
  readonly index_type: IndexType;
}

export interface ChunkHeader {
  readonly chunk_id: number;
  readonly storey_level: number;
  readonly quant_origin_mm: readonly [number, number, number];
  readonly quant_scale_mm: number;
  readonly bbox_mm: BboxMm;
  readonly vertices: BufferRange;
  readonly indices: IndexRange;
  readonly triangle_count: number;
}

export interface MeshBufferHeader {
  readonly mesh_buffer_version: number;
  readonly units: string;
  readonly vertex_layout: { readonly interleaved: boolean; readonly stride_bytes: number; readonly attributes: readonly VertexAttribute[] };
  readonly chunks: readonly ChunkHeader[];
  readonly guid_table: readonly string[];
}

export interface ManifestChunk {
  readonly chunk_id: number;
  readonly storey_level: number;
  readonly storey_name: string | null;
  readonly bbox_mm: BboxMm;
  readonly element_count: number;
}

export interface MeshManifest {
  readonly manifest_version: number;
  readonly model_bbox_mm: BboxMm;
  readonly chunks: readonly ManifestChunk[];
}

export interface ElementInfoEntry {
  readonly name: string | null;
  readonly ifc_class: string | null;
  readonly description: string | null;
}

export interface ElementInfoTable {
  readonly entry_count: number;
  readonly element_info: readonly ElementInfoEntry[];
}

export class IfcEngineError extends Error {
  readonly op: string;
  readonly raw: string;
  constructor(op: string, message: string, raw: string) {
    super(`${op}: ${message}`);
    this.name = "IfcEngineError";
    this.op = op;
    this.raw = raw;
  }
}

function toEngineError(op: string, thrown: unknown): IfcEngineError {
  if (thrown instanceof IfcEngineError) return thrown;
  const raw = typeof thrown === "string" ? thrown : thrown instanceof Error ? thrown.message : String(thrown);
  let message = raw;
  try {
    const parsed = JSON.parse(raw) as { error?: string };
    if (typeof parsed?.error === "string") message = parsed.error;
  } catch {
    // Not JSON (a panic string, a TypeError from the glue, ...): keep it verbatim.
  }
  return new IfcEngineError(op, message, raw);
}

function engineErrorEnvelope(value: unknown): string | null {
  if (typeof value === "object" && value !== null) {
    const inner = (value as Record<string, unknown>)["error"];
    if (typeof inner === "string") return inner;
  }
  return null;
}

const V1_VERTEX_ATTRIBUTES: readonly VertexAttribute[] = [
  { name: "position", type: "u16", count: 3, offset: 0 },
  { name: "normal_oct", type: "i8", count: 2, offset: 6 },
  { name: "instanceIndex", type: "u32", count: 1, offset: 8 },
];

function sameVertexAttributes(actual: readonly VertexAttribute[], expected: readonly VertexAttribute[]): boolean {
  if (actual.length !== expected.length) return false;
  return expected.every((exp) => actual.some((a) => a.name === exp.name && a.type === exp.type && a.count === exp.count && a.offset === exp.offset));
}

export function decodeChunkHeader(json: string): MeshBufferHeader {
  const header = JSON.parse(json) as MeshBufferHeader;
  const envelope = engineErrorEnvelope(header);
  if (envelope !== null) throw new IfcEngineError("mesh_chunk", envelope, json);
  if (header.mesh_buffer_version !== SUPPORTED_MESH_BUFFER_VERSION) {
    throw new Error(`unsupported mesh_buffer_version ${header.mesh_buffer_version} (this app decodes v${SUPPORTED_MESH_BUFFER_VERSION})`);
  }
  if (header.vertex_layout.stride_bytes !== VERTEX_STRIDE_BYTES || !header.vertex_layout.interleaved) {
    throw new Error(`unsupported vertex layout: interleaved=${String(header.vertex_layout.interleaved)} stride=${header.vertex_layout.stride_bytes}`);
  }
  if (!sameVertexAttributes(header.vertex_layout.attributes, V1_VERTEX_ATTRIBUTES)) {
    throw new Error(`unsupported vertex layout attributes: ${JSON.stringify(header.vertex_layout.attributes)}`);
  }
  return header;
}

function decodeManifest(json: string): MeshManifest {
  const manifest = JSON.parse(json) as MeshManifest;
  const envelope = engineErrorEnvelope(manifest);
  if (envelope !== null) throw new IfcEngineError("mesh_manifest", envelope, json);
  if (manifest.manifest_version !== SUPPORTED_MANIFEST_VERSION) {
    throw new Error(`unsupported manifest_version ${manifest.manifest_version} (this app reads v${SUPPORTED_MANIFEST_VERSION})`);
  }
  return manifest;
}

declare const sessionHandleBrand: unique symbol;
export type SessionHandle = number & { readonly [sessionHandleBrand]: true };

export interface RawChunk {
  readonly header: string;
  readonly blob: Uint8Array;
}

type WasmModule = typeof import("../vendor/ifc-mesh-wasm/ifc_mesh_wasm.js");

let wasmModule: Promise<WasmModule> | null = null;

function loadWasm(): Promise<WasmModule> {
  if (wasmModule === null) {
    wasmModule = (async () => {
      const mod = await import("../vendor/ifc-mesh-wasm/ifc_mesh_wasm.js");
      await mod.default();
      return mod;
    })().catch((cause: unknown) => {
      wasmModule = null;
      throw toEngineError("wasm_init", cause);
    });
  }
  return wasmModule;
}

export class WasmMeshSource {
  async open(bytes: Uint8Array): Promise<SessionHandle> {
    const wasm = await loadWasm();
    try {
      return wasm.open_ifc(bytes) as SessionHandle;
    } catch (cause) {
      throw toEngineError("open_ifc", cause);
    }
  }

  async manifest(handle: SessionHandle): Promise<MeshManifest> {
    const wasm = await loadWasm();
    try {
      return decodeManifest(wasm.mesh_manifest(handle));
    } catch (cause) {
      throw toEngineError("mesh_manifest", cause);
    }
  }

  async chunk(handle: SessionHandle, chunkId: number): Promise<RawChunk> {
    const wasm = await loadWasm();
    try {
      const result = wasm.mesh_chunk(handle, chunkId);
      try {
        return { header: result.header, blob: result.blob };
      } finally {
        result.free();
      }
    } catch (cause) {
      throw toEngineError("mesh_chunk", cause);
    }
  }

  async elementInfo(handle: SessionHandle): Promise<ElementInfoTable | null> {
    const wasm = await loadWasm();
    if (typeof (wasm as Record<string, unknown>)["mesh_session_element_info_candidate"] !== "function") return null;
    try {
      return JSON.parse(wasm.mesh_session_element_info_candidate(handle)) as ElementInfoTable;
    } catch (cause) {
      throw toEngineError("mesh_session_element_info_candidate", cause);
    }
  }

  async close(handle: SessionHandle): Promise<void> {
    const wasm = await loadWasm();
    wasm.close_ifc(handle);
  }
}
